import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CalendarDays, Check, EyeOff, Plus, RefreshCw, ShieldCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCalendarFlow, useResetOnBackForward } from "@/components/calendar/calendar-flow";
import { DisconnectCalendarDialog } from "@/components/calendar/connect-dialogs";
import { Badge, DarkPill, IconTile, OutlinePill } from "@/components/calendar/ui";
import { showPillToast } from "@/components/calendar/pill-toast";
import { useDisconnectCalendar, useStartGoogleConnect, useUpdateConnection } from "@/hooks/use-calendar";
import { returnUrl, saveHandoff } from "@/lib/calendar-handoff";

/**
 * Settings › Calendar (J15–J17): the connected account, the default calendar, the
 * free-time switch, and disconnect / reconnect. Must sit inside a
 * `<CalendarFlowProvider origin="settings">`. Renders nothing until the calendar API exists.
 */
export function CalendarSettingsSection() {
  const flow = useCalendarFlow();
  const update = useUpdateConnection();
  const disconnect = useDisconnectCalendar();
  const startConnect = useStartGoogleConnect();
  useResetOnBackForward(startConnect);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // /settings#calendar (from Momentum and Find a time) lands on this section.
  useEffect(() => {
    if (window.location.hash !== "#calendar") return;
    const t = window.setTimeout(() => sectionRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 150);
    return () => window.clearTimeout(t);
  }, [flow.available]);

  // Before the calendar API ships, Settings says so — like Reminders — instead of a connect flow that can't finish.
  if (!flow.available) {
    return (
      <section id="calendar">
        <h2 className="eyebrow mb-3 px-1">Calendar</h2>
        <div className="surface-card flex cursor-not-allowed items-center gap-3 px-6 py-4 opacity-60">
          <CalendarDays className="h-4 w-4 shrink-0 text-stone-400" />
          <span className="min-w-0 flex-1 text-sm font-medium text-foreground">Google Calendar</span>
          <span className="shrink-0 text-xs text-muted-foreground">Coming soon</span>
        </div>
      </section>
    );
  }
  const connection = flow.connection;

  /** Back through Google — to renew access, or to allow free/busy. */
  const goToGoogle = () => {
    saveHandoff({ origin: "settings", returnPath: "/settings" });
    startConnect.mutate({ returnTo: returnUrl("/settings") });
  };

  const ended = connection?.status === "needs_reconnect";
  // Success means the browser is on its way to Google; Back from there resets it (above).
  const leaving = startConnect.isPending || startConnect.isSuccess;

  return (
    <section ref={sectionRef} id="calendar" className="scroll-mt-24">
      <h2 className="eyebrow mb-3 px-1">Calendar</h2>
      <div className="surface-card overflow-hidden">
        {!connection ? (
          <div className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center">
            <IconTile>
              <CalendarDays className="h-[18px] w-[18px]" />
            </IconTile>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-medium text-foreground">Google Calendar</p>
              <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
                Add Next Steps to your calendar at times that fit your day. Not connected.
              </p>
            </div>
            <DarkPill onClick={flow.connect}>Connect</DarkPill>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-4 px-6 py-5">
              <IconTile>
                <CalendarDays className="h-[18px] w-[18px]" />
              </IconTile>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[15px] font-medium text-foreground">Google Calendar</p>
                  {ended ? (
                    <Badge tone="amber" icon={<AlertTriangle className="h-3.5 w-3.5" />}>
                      Needs reconnecting
                    </Badge>
                  ) : (
                    <Badge tone="green" icon={<Check className="h-3.5 w-3.5" />}>
                      Connected
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 truncate text-[13px] text-muted-foreground">{connection.account.email}</p>
              </div>
              {ended ? (
                <DarkPill loading={leaving} onClick={goToGoogle}>
                  {!leaving && <RefreshCw className="h-4 w-4" />}
                  Reconnect
                </DarkPill>
              ) : (
                <OutlinePill onClick={() => setConfirmDisconnect(true)}>Disconnect</OutlinePill>
              )}
            </div>

            {ended && (
              <p className="flex gap-2.5 border-t border-[#ecd28c]/60 bg-[#fbf3dc] px-6 py-3.5 text-[13.5px] leading-relaxed text-[#6d4f12]">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                Your Google Calendar connection has ended. Reconnect to keep adding Next Steps. Events already added are still
                in your calendar.
              </p>
            )}

            <div className="border-t border-black/5" />
            <div className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center">
              <CalendarDays className="hidden h-4 w-4 shrink-0 text-stone-400 sm:block" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">Default calendar</p>
                <p className="text-xs text-muted-foreground">Used first when you add a step. Changes to the last one you use.</p>
              </div>
              <Select
                value={connection.defaultCalendarId}
                onValueChange={(id) => update.mutate({ defaultCalendarId: id })}
                disabled={ended}
              >
                <SelectTrigger className="h-10 w-full rounded-xl border-foreground/10 bg-white text-[13.5px] sm:w-[240px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[70] rounded-xl border-foreground/[0.06] bg-white">
                  {connection.calendars.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="rounded-lg text-[13.5px]">
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border-t border-black/5" />
            <div className="flex items-center gap-3 px-6 py-4">
              <EyeOff className="hidden h-4 w-4 shrink-0 text-stone-400 sm:block" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">Suggest free times</p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {connection.access.freeBusy
                    ? "Checks when you're busy, never what your events are. Off: you pick times yourself."
                    : "Google hasn't allowed UnfoldWIN to see when you're busy. Turning this on asks Google once more."}
                </p>
              </div>
              <Switch
                aria-label="Suggest free times"
                checked={connection.access.freeBusy && connection.suggestFreeTimes}
                disabled={ended || update.isPending}
                onCheckedChange={(on) => {
                  if (on && !connection.access.freeBusy) goToGoogle();
                  else update.mutate({ suggestFreeTimes: on });
                }}
                className="h-6 w-11 data-[state=checked]:bg-primary [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-5"
              />
            </div>
          </>
        )}

        <div className="border-t border-black/5" />
        <div className="flex items-center gap-3 px-6 py-4">
          <Plus className="h-4 w-4 shrink-0 text-primary/50" />
          <span className="min-w-0 flex-1 text-sm font-medium text-primary/60">Connect another calendar</span>
          <Badge tone="muted">Outlook coming soon</Badge>
        </div>

        <div className="flex gap-3 border-t border-black/5 bg-primary/[0.05] px-6 py-5">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="text-[13.5px] leading-relaxed">
            <p className="font-semibold text-foreground">How UnfoldWIN uses your calendar</p>
            <p className="mt-1 text-foreground/80">
              UnfoldWIN sees your list of calendars so you can choose where a step goes and, if you allow it, when you&apos;re
              busy so it can suggest free times. It never sees the names, details or attendees of your events, and it only
              creates the events you schedule.
            </p>
            <p className="mt-2 text-muted-foreground">
              Disconnecting removes UnfoldWIN&apos;s access. Events already added stay in your calendar.
            </p>
          </div>
        </div>
      </div>

      {connection && (
        <DisconnectCalendarDialog
          open={confirmDisconnect}
          onOpenChange={setConfirmDisconnect}
          email={connection.account.email}
          pending={disconnect.isPending}
          onConfirm={() =>
            disconnect.mutate(undefined, {
              onSuccess: () => {
                setConfirmDisconnect(false);
                showPillToast({ message: "Google Calendar disconnected · events already added stay" });
              },
            })
          }
        />
      )}
    </section>
  );
}
